<script setup>
// The SLA report's filters (R4), 7-4 = A: five kit FilterSelects always on the bar — policy, inbox, agent, team and
// label — each opening its own searchable list in a popover that places itself. The "Add filter" cascade (and its
// fixed-position submenu that ran off the right edge) is gone. Same URL keys and the same `filterChange` payload.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import {
  parseFilterURLParams,
  generateCompleteURLParams,
} from '../../helpers/reportFilterHelper';
import FilterSelect from 'dashboard/components-next/polaris/cards/FilterSelect.vue';
import { Button } from 'dashboard/components-next/polaris/ui/button';

const emit = defineEmits(['filterChange']);

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

const appliedFilters = ref({
  assigned_agent_id: null,
  inbox_id: null,
  team_id: null,
  sla_policy_id: null,
  label_list: null,
});

const agents = computed(() => store.getters['agents/getAgents']);
const inboxes = computed(() => store.getters['inboxes/getInboxes']);
const teams = computed(() => store.getters['teams/getTeams']);
const labels = computed(() => store.getters['labels/getLabels']);
const sla = computed(() => store.getters['sla/getSLA']);

const byId = list => list.map(item => ({ value: item.id, label: item.name }));
const filters = computed(() => [
  {
    key: 'sla_policy_id',
    label: t('SLA_REPORTS.DROPDOWN.SLA'),
    options: byId(sla.value),
  },
  {
    key: 'inbox_id',
    label: t('SLA_REPORTS.DROPDOWN.INBOXES'),
    options: byId(inboxes.value),
  },
  {
    key: 'assigned_agent_id',
    label: t('SLA_REPORTS.DROPDOWN.AGENTS'),
    options: byId(agents.value),
  },
  {
    key: 'team_id',
    label: t('SLA_REPORTS.DROPDOWN.TEAMS'),
    options: byId(teams.value),
  },
  {
    // the label filter travels by its title, as before
    key: 'label_list',
    label: t('SLA_REPORTS.DROPDOWN.LABELS'),
    options: labels.value.map(item => ({
      value: item.title,
      label: item.title,
    })),
  },
]);

// URL values come back as text; ids are numbers, labels stay text.
const filterValue = key => {
  const value = appliedFilters.value[key];
  if (value === null || value === undefined || value === '') return '';
  return key === 'label_list' ? value : Number(value);
};

const hasActiveFilters = computed(() =>
  Object.values(appliedFilters.value).some(
    value => value !== null && value !== undefined && value !== ''
  )
);

const updateURLParams = () => {
  const params = generateCompleteURLParams({
    from: route.query.from,
    to: route.query.to,
    range: route.query.range,
    filters: {
      agent_id: appliedFilters.value.assigned_agent_id,
      inbox_id: appliedFilters.value.inbox_id,
      team_id: appliedFilters.value.team_id,
      sla_policy_id: appliedFilters.value.sla_policy_id,
      label: appliedFilters.value.label_list,
    },
  });
  router.replace({ query: params });
};

const emitChange = () => {
  updateURLParams();
  emit('filterChange', appliedFilters.value);
};

const setFilter = (key, value) => {
  appliedFilters.value[key] = value === '' ? null : value;
  emitChange();
};

const clearAllFilters = () => {
  appliedFilters.value = {
    assigned_agent_id: null,
    inbox_id: null,
    team_id: null,
    sla_policy_id: null,
    label_list: null,
  };
  emitChange();
};

const initializeFromURL = () => {
  const urlFilters = parseFilterURLParams(route.query);
  appliedFilters.value.assigned_agent_id = urlFilters.agent_id;
  appliedFilters.value.inbox_id = urlFilters.inbox_id;
  appliedFilters.value.team_id = urlFilters.team_id;
  appliedFilters.value.sla_policy_id = urlFilters.sla_policy_id;
  appliedFilters.value.label_list = urlFilters.label;
};

onMounted(() => {
  initializeFromURL();
  if (hasActiveFilters.value) {
    emitChange();
  }
});
</script>

<template>
  <FilterSelect
    v-for="filter in filters"
    :key="filter.key"
    :model-value="filterValue(filter.key)"
    :label="filter.label"
    :options="filter.options"
    :data-filter="filter.key"
    @update:model-value="value => setFilter(filter.key, value)"
  />
  <Button
    v-if="hasActiveFilters"
    variant="ghost"
    size="sm"
    @click="clearAllFilters"
  >
    {{ $t('SLA_REPORTS.DROPDOWN.CLEAR_ALL') }}
  </Button>
</template>
